const basico2 = {
    titulo: `JavaScript: promesas`,
    problemas: [
        {
            codigo: 1,
            descripcion: `Las promesas son la propuesta más moderna para el manejo de la asincronía en JavaScript. Veremos primero un problema básico sin promesas y luego como lo transformamos utilizando promesas.
Problema: Imaginemos que tenemos que implementar una aplicación que nos muestre por pantalla que debemos iniciar nuestra actividad deportiva, primero con 10 minutos de caminata, luego con otros 10 minutos de correr y así sucesivamente con saltar, pesas y bicicleta.
Solución: Primero lo vamos a resolver utilizando únicamente callbacks y la función setTimeout:
<code>
function temporizador(ms, fn) {
    setTimeout(() => fn(), ms)
}

temporizador(0, () => {
  console.log('caminar')
  temporizador(1000, () => {
    console.log('correr')
    temporizador(1000, () => {
      console.log('saltar')
      temporizador(1000, () => {
        console.log('pesas')
        temporizador(1000, () => {
          console.log('bicicleta')
        })
      })
    })
  })
})
</code>
Si bien podemos resolverlo definiendo temporizadores para cada actividad que no sean anidados, consideremos que cada vez que terminamos una actividad, en dicho momento definimos cuanto tiempo vamos a disponer para la siguiente actividad (puede depender de nuestro estado de cansancio)
Hay muchas situaciones en una aplicación web donde varias actividades asincronas se ejecutan en forma encadenada, debe terminarse una para que comience la siguiente, porque la primera tiene datos que requiere la segunda.
En nuestro problema inmediatamente veremos que la primera actividad "caminar", aparece en la consola e inmediatamente creamos otro temporizador para dentro de 1000 milisegundos, y así sucesivamente.
Este tipo de código se lo llama callback hell (infierno de llamadas), si bien podemos entenderlo, luego en la realidad los algoritmos tienen que procesar errores y muchas otras situaciones y se vuelven ilegibles.
`,
            programa: `const acciones=[]

function temporizador(ms, fn) {
  setTimeout(() => fn(), ms)
}

temporizador(0, () => {
  acciones.push('caminar')
  temporizador(100, () => {
    acciones.push('correr')
    temporizador(100, () => {
      acciones.push('saltar')
      temporizador(100, () => {
        acciones.push('pesas')
        temporizador(100, () => {
          acciones.push('bicicleta')
          console.log(acciones)
        })
      })
    })
  })
})

acciones.push("prepararse")`,
            solucion: "['prepararse', 'caminar', 'correr', 'saltar', 'pesas', 'bicicleta']",
        }, {
            codigo: 2,
            descripcion: `Las promesas son un nuevo método para procesar código asincrono en JavaScript. El abuso de funciones callback anidadas trae como consecuencia el callback hell, motivo por el cual el código se vuelve complicado e ilegible.
Una promesa representa una operación asincrona que puede ser resuelta o rechazada.    
Una analogía de una promesa es la de un jefe que le encarga una tarea a un empleado, el jefe luego se dedica a otras cosas, el empleado se encarga de ejecutar la tarea y cuando la finaliza le avisa al jefe (inclusive le avisa si no sabe hacer la tarea)
Veamos como resolver el mismo problema utilizando promesas:
<code>
function temporizador(ms) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(ms)
    }, ms)
  })
}

temporizador(0).then(x => {
  console.log('caminar')
  return temporizador(1000)
}).then(x => {
  console.log('correr')
  return temporizador(1000)
}).then(x => {
  console.log('saltar')
  return temporizador(1000)
}).then(x => {
  console.log('pesas')
  return temporizador(1000)
}).then(x => {
  console.log('bicicleta')
})
</code>
Para crear una promesa debemos crear una instancia de la clase 'Promise':
<code>
new Promise((resolve) => {
  setTimeout(() => {
      resolve(ms)
  }, ms)
})
</code>
El constructor recibe una función callback que implementa el algoritmo que normalmente llevara cierto tiempo (por ejemplo una función asincrona que hace una petición a un servicio web), en nuestro ejemplo lanzamos un temporizador.
Cuando la promesa se resuelve, la función callback debe llamar a la función resolve enviando los datos generados (no son obligatorios)
Ahora analicemos como podemos capturar la devolución de una promesa:
<code>
temporizador(0).then(x => {
  console.log('caminar')
  return temporizador(1000)
})
</code>
Cuando llamamos a la función temporizador le pasamos como parámetro el tiempo que debe esperar para resolver la promesa, y como la función temporizador retorna un objeto de la clase Promise podemos a partir de dicho objeto llamar al método then, que recibe una función callback que se ejecutará cuando la promesa se resuelva (la llama a la función resolve desde la promesa)
Luego dentro del método then retornamos otra promesa, que es la promesa que se retorna por el método then, y podrá ser capturado en forma encadenado:
<code>
temporizador(0).then(x => {
  console.log('caminar')
  return temporizador(1000)
}).then(x => {
  console.log('correr')
  return temporizador(1000)
}).then(x => {
  console.log('saltar')
  return temporizador(1000)
}).then(x => {
  console.log('pesas')
  return temporizador(1000)
}).then(x => {
  console.log('bicicleta')
})
    </code>
El último then no retorna una promesa. Como vemos es mucho más legible el código, y es más fácil de entender que los callbacks.`,
            programa: `const acciones = []

function temporizador(ms) {
  return new Promise((resolve) => {
    setTimeout(() => {
        resolve(ms)
    }, ms)
  })
}

temporizador(0).then(x => {
  acciones.push('caminar')
  return temporizador(100)
}).then(x => {
  acciones.push('correr')
  return temporizador(100)
}).then(x => {
  acciones.push('saltar')
  return temporizador(100)
}).then(x => {
  acciones.push('pesas')
  return temporizador(100)
}).then(x => {
  acciones.push('bicicleta')
  console.log(acciones)
})

acciones.push("prepararse")`,
            solucion: "['prepararse', 'caminar', 'correr', 'saltar', 'pesas', 'bicicleta']"
        }, {
            codigo: 3,
            descripcion: `Hasta ahora hemos visto la idea central en las promesas: programan el código para que se ejecute más tarde y podemos agregar funciones de devolución de llamada (callback) que se ejecutarán después de que se cumpla una promesa. Esto nos muestra por qué las promesas se llaman promesas: cuando creamos una, prometemos proporcionar un valor en algún momento en el futuro.
En los siguientes conceptos vamos a diseccionar el uso de promesas en JavaScript, solo se ha hecho una pequeña presentación y se ha mostrado en que tipo de problemas nos ayudarán en su solución.`,
            programa: `const arreglo = []

const promesa1 = new Promise((resolve) => {
  setTimeout(() => {
      resolve(10)
  }, 100)
}).then(dato => {
  arreglo.push(dato)
  arreglo.push(20)
  console.log(arreglo)
})

arreglo.push(1)`,
            solucion: "[1, 10, 20]"
        }, {
            codigo: 4,
            descripcion: ``,
            programa: `const arreglo = []

const promesa1 = new Promise((resolve) => {
  setTimeout(() => {
     resolve({ nombre: 'ana', clave: 'abc' })
  }, 100)
}).then(dato => {
  arreglo.push(dato)
  console.log(arreglo)
})

arreglo.push({ nombre: 'juan', clave: '123' })`,
            solucion: "[{nombre: 'juan', clave: '123'},{nombre: 'ana', clave: 'abc'}]"
        }, {
          codigo: 5,
          descripcion: `En forma más larga podemos llamar al then a partir del objeto creado y no encadenarlo (no es común este tipo de código)`,
          programa: `const arreglo = []

const promesa1 = new Promise((resolve) => {
  setTimeout(() => {
    resolve(10)
  }, 100)
})

promesa1.then(dato => {
  arreglo.push(dato)
  arreglo.push(20)
  console.log(arreglo)
})

arreglo.push(1)`,
          solucion: "[1, 10, 20]"
      }, {
        codigo: 6,
        descripcion: `Algo muy importante que hay que decir es que el constructor de la clase Promise se ejecuta en forma síncrona, donde luego con el setTimeout programamos una actividad asíncrona. La ejecución del método then siempre se ejecuta en forma asíncrona.
<code>
console.log('primero')

function temporizador(ms) {

  return new Promise((resolve) => {
    console.log('segundo')
    setTimeout(() => {
      resolve(ms)
    }, ms)
  })
}

temporizador(500).then(x => {
  console.log('cuarto')
})

console.log('tercero')
</code>
Como podemos comprobar el mensaje 'segundo' aparece antes del mensaje 'tercero', esto debido a que cuando creamos la promesa se ejecuta su constructor inmediatamente.
`,
        programa: `const arreglo = []
arreglo.push(1)
function temporizador(ms) {

  return new Promise((resolve) => {
    arreglo.push(2)
    setTimeout(() => {
      arreglo.push(4)
      resolve(ms)
    }, ms)
  })
}

temporizador(500).then(x => {
  arreglo.push(5)
  console.log(arreglo)
})

arreglo.push(3)`,
        solucion: "[1, 2, 3, 4, 5]"
    }
    ]
}

export default basico2;

